import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search, X, AlertCircle } from 'lucide-react';
import { referenceService } from '@/services/referenceService';
import { GroupedIcd10Code } from '@/types/reference.types';
import { groupByCategory } from '@/utils/groupIcd10Codes';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/utils/cn';
import { useToast } from '@/hooks/use-toast';

interface MalformationCodeAutocompleteProps {
  value?: string; // comma-separated ICD-10 codes, e.g. "Q00.0, Q21.0"
  onChange: (value: string) => void;
  placeholder?: string;
  isDisabled?: boolean;
  className?: string;
}

function parseCodes(value?: string): string[] {
  if (!value) {
    return [];
  }
  return value
    .split(',')
    .map(code => code.trim())
    .filter(Boolean);
}

export function MalformationCodeAutocomplete({
  value,
  onChange,
  placeholder = 'Caută cod ICD-10 malformație sau descriere...',
  isDisabled = false,
  className
}: MalformationCodeAutocompleteProps) {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [codes, setCodes] = useState<GroupedIcd10Code[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedCodeStrings = useMemo(() => parseCodes(value), [value]);

  useEffect(() => {
    loadCodes();
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadCodes = async () => {
    setIsLoading(true);
    try {
      const data = await referenceService.getMalformationCodes();
      setCodes(data);
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca codurile de malformații. Vă rugăm încercați din nou.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const codeByValue = useMemo(() => {
    const map = new Map<string, GroupedIcd10Code>();
    codes.forEach(code => map.set(code.icd10_code, code));
    return map;
  }, [codes]);

  // Available (not yet selected) codes, filtered by search term
  const filteredCodes = useMemo(() => {
    const available = codes.filter(code => !selectedCodeStrings.includes(code.icd10_code));

    if (!searchTerm.trim()) {
      return available;
    }

    const term = searchTerm.toLowerCase().trim();
    return available.filter(code =>
      code.icd10_code.toLowerCase().includes(term) ||
      code.description_ro.toLowerCase().includes(term) ||
      code.category_group.toLowerCase().includes(term)
    );
  }, [codes, searchTerm, selectedCodeStrings]);

  const groupedCodes = useMemo(() => groupByCategory(filteredCodes), [filteredCodes]);

  const addCode = (code: GroupedIcd10Code) => {
    if (selectedCodeStrings.includes(code.icd10_code)) {
      return;
    }
    const next = [...selectedCodeStrings, code.icd10_code];
    onChange(next.join(', '));
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const removeCode = (codeToRemove: string) => {
    const next = selectedCodeStrings.filter(c => c !== codeToRemove);
    onChange(next.join(', '));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen && e.key !== 'Escape') {
      setIsOpen(true);
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredCodes.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredCodes.length) {
          addCode(filteredCodes[highlightedIndex]);
        }
        break;
      case 'Backspace':
        if (!searchTerm && selectedCodeStrings.length > 0) {
          removeCode(selectedCodeStrings[selectedCodeStrings.length - 1]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const highlightedElement = dropdownRef.current.querySelector(
        `[data-flat-index="${highlightedIndex}"]`
      ) as HTMLElement | null;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest', behavior: 'auto' });
      }
    }
  }, [highlightedIndex]);

  let flatIndex = -1;

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      {selectedCodeStrings.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selectedCodeStrings.map((codeString) => {
            const match = codeByValue.get(codeString);
            return (
              <Badge key={codeString} variant="secondary" className="gap-1 pr-1">
                <span>
                  {codeString}
                  {match ? ` - ${match.description_ro}` : ''}
                </span>
                {!isDisabled && (
                  <button
                    type="button"
                    onClick={() => removeCode(codeString)}
                    className="ml-1 rounded-full hover:bg-muted-foreground/20"
                    aria-label={`Elimină codul ${codeString}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </Badge>
            );
          })}
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={isDisabled || isLoading}
          className="pl-10"
        />
      </div>

      {isOpen && !isDisabled && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-md max-h-[320px] overflow-y-auto"
        >
          {isLoading ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              Se încarcă...
            </div>
          ) : filteredCodes.length === 0 ? (
            <div className="p-4 text-center">
              <AlertCircle className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {searchTerm ? 'Nu s-au găsit rezultate' : 'Nu mai sunt coduri disponibile'}
              </p>
            </div>
          ) : (
            groupedCodes.map((group) => (
              <div key={group.category}>
                <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50 sticky top-0">
                  {group.category}
                </div>
                {group.items.map((code) => {
                  flatIndex += 1;
                  const index = flatIndex;
                  const isExtension = code.code_level === 'extensie';
                  return (
                    <div
                      key={code.icd10_code}
                      data-flat-index={index}
                      className={cn(
                        'px-3 py-2 cursor-pointer text-sm transition-colors text-foreground',
                        'hover:bg-accent hover:text-accent-foreground',
                        isExtension && 'pl-7 border-l-2 border-muted ml-3',
                        highlightedIndex === index && 'bg-accent text-accent-foreground'
                      )}
                      onClick={() => addCode(code)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                    >
                      <div className="font-medium text-foreground flex items-center">
                        {isExtension && (
                          <span className="mr-1.5 text-xs text-muted-foreground" aria-hidden="true">
                            ↳
                          </span>
                        )}
                        {code.icd10_code}
                        {code.code_level === 'categorie' && (
                          <span className="ml-2 text-xs font-normal text-muted-foreground">
                            (categorie generală)
                          </span>
                        )}
                        {isExtension && code.base_code && (
                          <span className="ml-2 text-xs font-normal text-muted-foreground">
                            (subcod pentru {code.base_code})
                          </span>
                        )}
                      </div>
                      <div className={cn('text-foreground/70 truncate', isExtension && 'text-xs')}>
                        {code.description_ro}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
